// 到達確認 (ADR-0014 §1-2・受け口の形は ADR-0018 §1-4)。
// 細部と判断の経緯は docs/design/0005-本番でWebSocketフォールバックと到達確認をどう実装するか.md §4-A。
//
// UDP が通らない端末からは通話サーバに**何も届かない**ので、ブラウザが HTTPS (TCP) で
// 「繋がったか・どこで落ちたか」を送るのがサーバ側の唯一の観測手段 (ADR-0010 §5-1)。
// 受け口は nginx の access_log だけで、アプリケーションサーバは作らない。
//
// **1 ページにつき 1 件だけ送る** (2026-10-16 のユーザー判断。ADR-0014 §6 の未決 4)。
// 「ページを開いた時点で UDP が使えるかどうかが分かるなら、その 1 回でよい」ため、
// 再試行・再接続では送り直さない。**率を出すときの母数は「ページを開いた回数」**で、
// 途中で方針を変えたら日付を記録すること (母数の意味が変わる)。
//
// **本文ではなくクエリ文字列で送る。** nginx は `return 204` のとき本文を読まないので、
// 本文に入れると記録に残らない。送る中身に秘密を含めないので URL に載せてよい。

/** 送る形の版。項目を増やしたら上げる。 */
export const REACH_FORMAT_VERSION = 1;

/** 受け口のパス。nginx 側は `location = /r` (docker/web/nginx/default.conf.template)。 */
export const REACH_PATH = "/r";

/** 溜めておける件数の上限。これを超えたら**古いほうから捨てる**。 */
export const REACH_QUEUE_MAX = 20;

const QUEUE_KEY = "telephone.reach.queue";

export type Route = "wt" | "ws";

/**
 * 失敗の段階。**ここが切り分けの要**（DESIGN-0005 §4-A）。
 *
 * - `timeout`      … 応答が無い。**UDP が捨てられている疑いが濃い**（R1 の主信号）
 * - `cert`         … 証明書で断られた。**UDP は通っている**（握手の返事が来ている）
 * - `refused`      … 手前の機器が明示的に拒否した
 * - `unsupported`  … そのブラウザが経路そのものに対応していない
 * - `other`        … 上のどれでもない
 */
export type Stage = "timeout" | "cert" | "refused" | "unsupported" | "other";

/** 回線種別。**3 値だけ**にする (ADR-0014 §5-2。キャリア名や SSID は送らない)。 */
export type NetworkKind = "wifi" | "cellular" | "other";

export interface Attempt {
  route: Route;
  /** 試したポート。URL から読めなければ null。 */
  port: number | null;
  ok: boolean;
  /** 失敗の段階。`ok` のときは付けない。 */
  stage?: Stage;
  /** 所要時間 (ミリ秒)。 */
  elapsedMs: number;
  network: NetworkKind;
  /** アプリの版。ビルド時に埋めたもの。 */
  appVersion: string;
  /**
   * 溜めてから送るまでの遅れ (秒)。**その場で送れたときは付けない**。
   *
   * nginx の記録に残るのは**到着時刻**なので、これが無いと前回の起動ぶんが
   * 今回の時刻として数えられてしまう (A5 で溜めて次の起動で送るため)。
   */
  delayedSec?: number;
}

/**
 * 送る中身を作る。**入室できたかは入れない。**
 *
 * ADR-0014 §5-2 は「入室できたか」も項目に挙げているが、**1 件 = ページを開いた 1 回**
 * （上記）にしたので、送る時点ではまだ入室していない。入室は別の段階なので、
 * 必要になったら版 (`v`) を上げて足す。
 */
export function buildQuery(a: Attempt): string {
  const q = new URLSearchParams();
  q.set("v", String(REACH_FORMAT_VERSION));
  q.set("r", a.route);
  if (a.port !== null) q.set("p", String(a.port));
  q.set("ok", a.ok ? "1" : "0");
  if (!a.ok && a.stage) q.set("st", a.stage);
  q.set("ms", String(Math.round(a.elapsedMs)));
  q.set("n", a.network);
  q.set("a", a.appVersion);
  if (a.delayedSec !== undefined && a.delayedSec > 0) q.set("d", String(Math.round(a.delayedSec)));
  return q.toString();
}

/** 試したポート。`https://host:4434` → 4434。書いていなければ 443 (https の既定)。 */
export function portOf(url: string): number | null {
  try {
    const u = new URL(url);
    if (u.port) return Number(u.port);
    return u.protocol === "https:" ? 443 : null;
  } catch {
    return null;
  }
}

/**
 * 失敗の段階を見分ける。
 *
 * **段階が確かなもの（時間切れ・非対応）は投げる側が `stage` を付けて渡す**
 * （transport.ts の {@link import("./transport.ts").TransportError}）。ここに来るのは
 * ブラウザが投げた `WebTransportError` などで、**文面しか手掛かりが無い**。
 * ブラウザごとに文面が違うので、取り違えたときは `other` に寄せる。
 */
export function classifyStage(e: unknown): Stage {
  const tagged = (e as { stage?: unknown } | null)?.stage;
  if (
    tagged === "timeout" ||
    tagged === "cert" ||
    tagged === "refused" ||
    tagged === "unsupported" ||
    tagged === "other"
  ) {
    return tagged;
  }
  const text = String((e as { message?: unknown } | null)?.message ?? e).toLowerCase();
  if (text.includes("certificate") || text.includes("cert_") || text.includes("cert ")) return "cert";
  if (text.includes("refused")) return "refused";
  if (text.includes("not support") || text.includes("unsupported")) return "unsupported";
  if (text.includes("timeout") || text.includes("timed out")) return "timeout";
  return "other";
}

/** 回線種別。取れないブラウザが多いので、**取れなければ `other`**。 */
export function networkKind(connection: unknown): NetworkKind {
  const type = (connection as { type?: unknown } | null | undefined)?.type;
  if (type === "wifi") return "wifi";
  if (type === "cellular") return "cellular";
  return "other";
}

/** `navigator.connection` を読む (型が無いブラウザがあるので any 相当で受ける)。 */
function currentNetworkKind(): NetworkKind {
  return networkKind((navigator as { connection?: unknown }).connection);
}

/** ページを開いてから 1 件送ったか。**2 件目は送らない**（上記の方針）。 */
let sentThisPage = false;

/** 溜めてあるぶんを読む。壊れていたら捨てる (localStorage は人が触れる)。 */
function loadQueue(): { q: string; at: number }[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is { q: string; at: number } =>
        typeof (x as { q?: unknown }).q === "string" && typeof (x as { at?: unknown }).at === "number",
    );
  } catch {
    return [];
  }
}

function saveQueue(items: { q: string; at: number }[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-REACH_QUEUE_MAX)));
  } catch {
    // 容量がいっぱい・プライベートモードなど。到達確認は落ちてよい情報なので黙って諦める
  }
}

/** 1 件を実際に送る。成功したら true。 */
async function send(query: string): Promise<boolean> {
  try {
    // no-cors: 応答は読まない (204 なので中身も無い)。keepalive: ページを閉じても送り切る
    await fetch(`${REACH_PATH}?${query}`, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      keepalive: true,
      credentials: "omit",
      redirect: "follow",
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * 溜めてあるぶんを送る (A5)。**起動時に 1 回呼ぶ。**
 *
 * 送れなかったぶんは溜めたまま残す。{@link sentThisPage} とは別枠で、
 * 「1 ページ 1 件」の数には入れない (前回の起動ぶんなので)。
 */
export async function flushQueued(now: () => number = Date.now): Promise<number> {
  const items = loadQueue();
  if (items.length === 0) return 0;
  const left: { q: string; at: number }[] = [];
  let sent = 0;
  for (const item of items) {
    // 溜めたぶんは**必ず遅れを付け直す**。付いていたら上書きする (送るたびに遅れが伸びる)
    const q = new URLSearchParams(item.q);
    q.set("d", String(Math.max(0, Math.round((now() - item.at) / 1000))));
    if (await send(q.toString())) sent += 1;
    else left.push(item);
  }
  saveQueue(left);
  return sent;
}

/**
 * 今回の接続の試みを 1 件送る (A4)。**ページにつき 1 回だけ**。
 *
 * 送れなかったら端末に溜めて、次の起動で {@link flushQueued} が送る。
 * 戻り値は「送ったか」ではなく「**記録したか**」(溜めたぶんも true)。
 */
export async function reportAttempt(
  attempt: Omit<Attempt, "network" | "appVersion">,
  options: { appVersion: string; now?: () => number },
): Promise<boolean> {
  if (sentThisPage) return false;
  sentThisPage = true;
  const now = options.now ?? Date.now;
  const query = buildQuery({
    ...attempt,
    network: currentNetworkKind(),
    appVersion: options.appVersion,
  });
  if (await send(query)) return true;
  saveQueue([...loadQueue(), { q: query, at: now() }]);
  return true;
}
